import {inject, Injectable,} from '@angular/core';
import {DatePipe} from '@angular/common';
import moment from 'moment';
import {
  FIRST_20_NUMBERS_IN_WORDS,
  INPUT_FIELD_TYPES,
  IRREGULAR_SINGULAR_PLURAL_PAIRS,
  PLURAL_TO_SINGULAR_ENDINGS_PAIRS,
  PLURAL_TO_SINGULAR_PAIRS,
  SINGULAR_TO_PLURAL_PAIRS,
  TENTHS,
  UNCOUNTABLE_WORDS
} from "../shared/constants/data.constants";
import {ValueType} from "../common/interfaces/data.interfaces";

@Injectable({
  providedIn: 'root',
})
export class DataService {
  datePipe = inject(DatePipe);

  //types
  isArray = (x): boolean => Array.isArray(x);

  isBool = (x): boolean => typeof x === 'boolean';

  isNumber = (x): boolean => typeof x === 'number';

  isString = (x): boolean => typeof x === 'string' && !!x?.length;

  isObject_ = (x): boolean => (typeof x === 'function') || (typeof x === 'object');

  isNotDefined = (x) => x === undefined || x === null || x === '';

  isDefined = (x) => this.isValue && x !== undefined && x !== null && x !== '';

  isValue = (value: any) => !!value || value === false || value === 0;

  isPlainValue = (x) => this.isString(x) || this.isNumber(x) || this.isBool(x);

  isObject = (x) => this.isObject_(x) && !this.isString(x) && !this.isNumber(x)
    && !this.isArray(x) && this.isDefined(x);

  isDataInputType = (x: string) => !!INPUT_FIELD_TYPES.find(type => type === x);

  isNullOrUndefined(value: any) {
    return value === 'undefined' || value === 'null'
      || value === undefined || typeof value === 'undefined'
      || value === null || Number.isNaN(value)
      || (typeof value === 'object' && Object.keys(value).length === 0)
      || (typeof value === 'string' && value.trim().length === 0);
  }

  getType(value): ValueType {
    if (this.isArray(value)) return 'array';
    if (this.isString(value)) return 'string';
    if (this.isObject(value)) return 'object';
    if (this.isNumber(value)) return 'number';
    if (this.isBool(value)) return 'boolean';
    return null;
  }

  // dates
  getDisplayDate = (date: string) => new DatePipe('en-GB').transform(date, ' dd MMM y');

  getDisplayDateTime = (date): string => date.toString() === '2023-04-19'.length ?
    new DatePipe('en-GB').transform(date, ' dd MMM y') :
    new DatePipe('en-GB').transform(date, ' dd MMM y | HH:mm');

  formatDate = (d: any, format = 'yyyy-MM-dd'): string => this.datePipe.transform(d, format);

  getTodayDate = () => new Date().toISOString().slice(0, 10);

  getDateFromDays(date?, days?) {
    const result = new Date(date ?? new Date());
    result.setDate(result.getDate() + (days ?? 0));
    return result;
  }

  getDaysBetween(StartDate, EndDate) {
    if (!StartDate?.split('-').length || !EndDate?.split('-').length) return undefined;
    // The number of milliseconds in all UTC days (no DST)
    const oneDay = 1000 * 60 * 60 * 24;
    // A day in UTC always lasts 24 hours (unlike in other time formats)
    const start = Date.UTC(EndDate.split('-')[0], EndDate.split('-')[1], EndDate.split('-')[2]);
    const end = Date.UTC(StartDate.split('-')[0], StartDate.split('-')[1], StartDate.split('-')[2]);
    // so it's safe to divide by 24 hours
    return (start - end) / oneDay;
  }

  // isDate(value){
  //   if(this.countOccurrences(value, ':') === 2 ) return true;
  //   if(value && this.countOccurrences(value, '-') === 2 && value.length === 10 ) return true;
  // }

  isValidDate(value: string) {
    if (!value || value?.toString().length < 10) return false;

    let isDate = false;
    const formats = [
      moment.ISO_8601,
      'MM/DD/YYYY  :)  HH*mm*ss',
      'MM/DD/YYYY', 'MM-DD-YYYY',
      'DD/MM/YYYY', 'DD-MM-YYYY',
      'YYYY/MM/DD', 'YYYY-MM-DD',
    ];

    isDate = moment(value, formats, true).isValid();
    return isDate;
  }

  addMonthsToDate(date_, numOfMonths: number) {
    if (!numOfMonths) return date_;
    const date = new Date(date_);
    const dateCopy = new Date(date.getTime());
    dateCopy.setMonth(dateCopy.getMonth() + numOfMonths);
    return dateCopy;
  }

  formatIfDate(value) {
    return this.isValidDate(value) ? this.formatDate(value) : value;
  }


  //end dates


  // numbers
  getRandomNumberBetween = (min, max) => Math.floor(Math.random() * (max - min + 1) + min);

  roundToNearest = (n?: number, x?: number): number => n ? Math.ceil(n / x) * x : 0;

  randomNumbers = (min, max) => Math.round(Math.random() * (max - min)) + min;

  separateThousands = (n, separator = ',') => n.toString().replace(/\B(?=(\d3)+(?!\d))/g, separator);

  formatPhoneNumber(phone) {
    const str = phone?.toString();
    if (!(str?.length > 0) || !phone) return phone;
    if (str.indexOf('0') === 0 || str.indexOf('+') === 0) phone = this.setCharAt(str, 0, '255');
    return phone;
  }

  separateThousandsIfNumber(value, separator?) {
    // return number.toString().replace(/\B(?=(\d3)+(?!\d))/g, separator ?? ",");
    if (!value || (typeof value !== 'string' && typeof value !== 'number')) return value;

    return Number(value ?? 0) > 999 ? value?.toString()?.split('.')[0]
      ?.toString()?.replace(/(\d)(?=(\d{3})+(?!\d))/g, separator ?? '$1,') : value;
  }

  //  end number


  // arrays
  mergeUnique = (a1: any[], a2: any[]) => [...new Set([...a1, ...a2])];

  enumToObjectArray(enum_obj: any): { name: string, key: string }[] {
    const array = [];
    for (const key in enum_obj) array.push({name: key, value: enum_obj[key]});
    return array;
  }

  enumToArray(enum_obj: any): any[] {
    const array = [];
    for (const key in enum_obj) array.push(enum_obj[key]);
    return array;
  }

  mergeUniqueObjects(key: string, ...arrays: any[][]) {
    let uniqueArray = [...arrays?.[0]], value;

    arrays.slice(1).forEach(array => array.forEach(item => {
      const valueNotPresent = ((value = item?.[key]) && !uniqueArray.find(i => i?.[key] == value));
      if (valueNotPresent || !item.hasOwnProperty(key)) uniqueArray.push(item);
    }));

    return uniqueArray;
  }

  arrayIncludesBoth(array1: any[], array2: any[]) {
    if (!Array.isArray(array1) || !Array.isArray(array2)) return false;
    let includes = true;
    array2.forEach(item => includes = includes && array1.includes(item));
    return includes;
  }

  objectArrayToValues(array: any[], key: string) {
    if (!Array.isArray(array)) return false;
    return array.map(item => item?.[key]);
  }

  arrayIncludesEither(arr1, arr2) {
    const set = new Set(arr1);
    arr1 = arr1.map(a => a?.toLowerCase());
    arr2 = arr2.map(a => a?.toLowerCase());
    for (const item of arr2) if (set.has(item)) return true;
    return false;
  }

  removeDuplicates(array: any[], property: any = null): any[] {
    if (array === null) {
      return [];
    }

    return array.filter((item, index, self) => {
      if (property != null) {
        const clone = [...array];
        if (!clone[0].hasOwnProperty(property)) {
          throw new Error(`Could not find property [${property}] in Object Array`);
        }
        return index === self.findIndex((t) => (t[property] === item[property]));
      } else {
        const _item = JSON.stringify(item);
        return index === array.findIndex(obj => JSON.stringify(obj) === _item);
      }
    });
  }

  sortArray(array: any[], property: any, order: 'ASC' | 'DESC' = 'ASC'): any[] {
    try {
      if (array === null || property === null) return array;
      const pos = order === 'ASC' ? 1 : -1;
      const neg = order === 'ASC' ? -1 : 1;
      return array.slice().sort((firstEl, secondEl) => {
        if (firstEl[property] < secondEl[property]) return neg;
        if (firstEl[property] > secondEl[property]) return pos;
        return 0;
      });

    } catch (e) {
      console.error(e);
      return [];
    }
  }


  arrayToString(array: string[], sep = ' '): string {
    if (!Array.isArray(array)) return array;
    return this.replaceWords(array.toString(), ',', sep);
  }


  arrayToDisplayString(array: string[], lastSep = 'and'): string {
    if (!Array.isArray(array)) return array;
    let s = array.map(s => ' ' + s).toString();
    s = s.substring(0, s.lastIndexOf(',')) + ' ' + lastSep + s.substring(s.lastIndexOf(',') + 1);
    return s;
  }

  itemCloserTo(items, key, value) {
    const closestItem = items.reduce((closest, current) => {
      const closestDiff = Math.abs(closest?.[key] - value);
      const currentDiff = Math.abs(current?.[key] - value);
      return currentDiff < closestDiff ? current : closest;
    });

    return closestItem;
  }

  // end arrays //


  // objects
  hasSameProps = (obj1, obj2) => Object.keys(obj1).every((prop) => obj2.hasOwnProperty(prop));

  isObjectEmpty = (obj) => Object.keys(obj).length === 0;

  hasAllPropsIn(obj1, obj2) {
    let same = true;
    if (typeof obj2 !== 'object' || !obj2 || !obj1) return false;

    Object.keys(obj1).forEach((key): any => {
      if (!obj1[key]) return false;
      same = same && obj1[key] === obj2[key];
    });

    return same;
  }

  copyFields(obj2, fields: any[]) {
    const obj1: any = {};
    if (typeof obj2 !== 'object' || !obj2) return;

    fields.forEach(key => {
      if (!obj2[key]) return;
      obj1[key] = obj2[key];
    });

    return obj1;
  }

  hasProp(object, keys: string | string[]) {
    if (typeof keys !== 'string' && !Array.isArray(keys)) return false;
    let hasKey = false;

    typeof keys === 'string' ?
      hasKey = !!object[keys] :
      [...keys].forEach(key => hasKey = hasKey || !!object[key]);

    return hasKey;
  }

  hasCommonFieldValue(obj1, obj2, fields: string[]) {
    for (const field of fields) {
      if (obj1?.[field] === obj2?.[field]) {
        return true;
      }
    }
    return false;
  }

  getFirstPresentKeyValue(object, keys): any {
    let value = undefined;
    keys.forEach(key => !!object[key] && (value = object[key]));
    return value;
  }

  getFirstPresentKey(object, keys): any {
    let key = undefined;
    keys.forEach(key_ => (!!object[key_]) && (key = key_));
    return key;
  }

  // end objects


  // strings
  countOccurrences = (s, find) => (typeof s !== 'string') ? 0 : (s.split(find)).length - 1;

  replaceSpaces = (s, char = '') => (typeof s !== 'string') ? s : s.replace(/\s+/g, char);

  toCamelCase = (s) => s.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());

  trim = (s) => (typeof s !== 'string') ? s : s.replace(/\s+/g, '');

  capitalizeFirstLetter = (s): string => !s ? '' : s?.charAt(0).toUpperCase() + s?.slice(1);

  setCharAt(str, index, chr) {
    return (index > str.length - 1) ? str : str.substring(0, index) + chr + str.substring(index + 1);
  }

  truncate(str, num) {
    if (!str) return str;
    if (str.length > num) return str.slice(0, num) + '...';
    else return str;
  }

  valueIsEither(value, values: any[]): boolean {
    if (!Array.isArray(values) || !value) return false;
    return !!values.find(v => v === value);
  }

  valueIncludesEither(value, values: string[]): boolean {
    let includes = false;
    if (typeof value !== 'string') return false;
    values?.forEach(v => includes = includes ||
      (
        v?.includes('.') ?
          v?.toLowerCase()?.replace('.', '') === value?.toLowerCase() :
          value?.toLowerCase()?.includes(v?.toLowerCase())
      )
    );
    return includes;
  }

  toSingular(word: string, amount?: number): string {
    if (amount !== undefined && amount > 1) return this.toPlural(word);
    // save some time in the case that singular and plural are the same
    if (UNCOUNTABLE_WORDS.indexOf(word.toLowerCase()) >= 0) return word;

    // check for irregular forms
    for (const w in IRREGULAR_SINGULAR_PLURAL_PAIRS) {
      const pattern = new RegExp(`${IRREGULAR_SINGULAR_PLURAL_PAIRS[w]}$`, 'i');
      const replace = w;
      if (pattern.test(word)) return word.replace(pattern, replace);
    }

    // check for matches using regular expressions
    for (const reg in PLURAL_TO_SINGULAR_PAIRS) {
      const pattern = new RegExp(reg, 'i');
      if (pattern.test(word)) return word.replace(pattern, PLURAL_TO_SINGULAR_PAIRS[reg]);
    }
    return word;
  }

  singularize(word) {
    return word.replace(
      new RegExp(`(${Object.keys(PLURAL_TO_SINGULAR_ENDINGS_PAIRS).join('|')})$`),
      r => PLURAL_TO_SINGULAR_ENDINGS_PAIRS[r]
    );
  }

  toPlural(word: string, amount?: number): string {
    if (amount !== undefined && amount === 1) return this.toSingular(word);
    // save some time in the case that singular and plural are the same
    if (UNCOUNTABLE_WORDS.indexOf(word.toLowerCase()) >= 0) return word;

    // check for irregular forms
    for (const w in IRREGULAR_SINGULAR_PLURAL_PAIRS) {
      const pattern = new RegExp(`${w}$`, 'i');
      const replace = IRREGULAR_SINGULAR_PLURAL_PAIRS[w];
      if (pattern.test(word)) return word.replace(pattern, replace);
    }

    // check for matches using regular expressions
    for (const reg in SINGULAR_TO_PLURAL_PAIRS) {
      const pattern = new RegExp(reg, 'i');
      if (pattern.test(word)) return word.replace(pattern, SINGULAR_TO_PLURAL_PAIRS[reg]);
    }
    return word;
  }

  getStringBetween(str: string, char: string): any {
    str = str?.toString();
    if (!str?.includes(char)) return '';
    let lastIndex = str?.lastIndexOf(char);
    const specials = ['()', '{}', '[]', '<>', '--', '**', '!!', '__', '++'];

    if (this.valueIsEither(char, specials.map(e => e?.[0]))) {
      lastIndex = str.lastIndexOf(specials?.find(e => this.valueIncludes(e, char[0]))?.[1]);
    }

    return str?.substring(str?.indexOf(char) + 1, lastIndex);
  }

  valueIncludes(value, values: string[] | string, ignoreDot = false): boolean {
    let includes = false;
    if (!value || typeof value !== 'string') return false;
    if (Array.isArray(values)) values?.forEach(v => includes = includes ||
      (
        v?.toString()?.includes('.') && !ignoreDot ?
          v?.toString()?.toLowerCase()?.replace('.', '') === value?.toString()?.toLowerCase() :
          value?.toString()?.toLowerCase()?.includes(v?.toString()?.toLowerCase())
      )
    );

    else includes = values?.toString()?.includes('.') && !ignoreDot ?
      values?.toString()?.toLowerCase()?.replace('.', '') === value?.toLowerCase() :
      value?.toString()?.toLowerCase()?.includes(values?.toLowerCase());

    return includes;
  }

  replaceWords(str, find, replace = '') {
    if (!str || typeof str !== 'string' || !find || !replace) return '';
    if (Array.isArray(find)) find.forEach(s => str = str.replace(new RegExp(s, 'g'), replace));
    else str = str?.replace(new RegExp(find, 'g'), replace);
    return str;
  }


  valueIncluded(value, values: string[] | string): boolean {
    let includes = false;
    if (Array.isArray(values)) values?.forEach(v => includes = includes ||
      (
        value?.includes('.') ? value?.toLowerCase()?.replace('.', '') === v?.toLowerCase() :
          v?.toLowerCase()?.includes(value?.toLowerCase())
      )
    );

    else includes = value?.includes('.') ?
      value?.toLowerCase()?.replace('.', '') === values?.toLowerCase() :
      values?.toLowerCase()?.includes(value?.toLowerCase());

    return includes;
  }

  valueIncludedInEither(value, values: string[]): boolean {
    let includes = false;
    values.forEach(v => includes = includes || v.toLowerCase().includes(value.toLowerCase()));
    return includes;
  }


  camelToSpaced(y) {
    if (typeof y !== 'string' || y.includes(' ')) return y;
    return y.replace(/([A-Z]+)/g, ' $1').replace(/([A-Z][a-z])/g, '$1');
  }

  camelToTitleCase(string: string): string {
    if (typeof string !== 'string') return string;
    return (string.charAt(0).toUpperCase() + string.slice(1))
      .replace(/([A-Z]+)/g, ' $1').replace(/([A-Z][a-z])/g, '$1');
  }

  toTitleCase(text) {
    if (!text) return '';
    let i, j, str, lowers, uppers;

    str = text.replace(/([^\W_]+[^\s-]*) */g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });

    // left lowercase unless they are the first or last words in the string
    lowers = ['A', 'An', 'The', 'And', 'But', 'Or', 'For', 'Nor', 'As', 'At',
      'By', 'For', 'From', 'In', 'Into', 'Near', 'Of', 'On', 'Onto', 'To', 'With'];
    for (i = 0, j = lowers.length; i < j; i++) {
      str = str.replace(new RegExp('\\s' + lowers[i] + '\\s', 'g'), (txt) => txt.toLowerCase());
    }

    // Certain words such as initialisms or acronyms should be left uppercase
    uppers = ['Id', 'Tv'];
    for (i = 0, j = uppers.length; i < j; i++) {
      str = str.replace(new RegExp('\\b' + uppers[i] + '\\b', 'g'), uppers[i].toUpperCase());
    }

    return str;
  }

  numberTowords(n) {
    if (n < 20) return FIRST_20_NUMBERS_IN_WORDS[n];
    const digit = n % 10;

    let prefix = TENTHS[~~(n / 10) - 2];
    if (n < 100) return prefix + (digit ? '-' + FIRST_20_NUMBERS_IN_WORDS[digit] : ' ');

    prefix = FIRST_20_NUMBERS_IN_WORDS[~~(n / 100)] + ' hundred';
    if (n < 1000) return prefix + (n % 100 == 0 ? ' ' : ' and ' + this.numberTowords(n % 100));

    prefix = this.numberTowords(~~(n / 1000)) + ' thousand';
    return prefix + (n % 1000 != 0 ? ' ' + this.numberTowords(n % 1000) : '');
  }

  removeLeadingPhoneCode(phone) {
    const str = phone?.toString();
    if (!(str?.length > 0) || !phone) return phone;
    if (str.indexOf('0') === 0) this.setCharAt(str, 0, '');
    if (str.indexOf('+255') === 0) return str?.replace('+255', '');
    if (str.indexOf('255') === 0) return str?.replace('255', '');
  }

  isAllNumbersWithLengthBetween(val: string, n, m) {
    const isnum = /^\d+$/.test(val);
    if (val.length >= n && val.length <= m && isnum) return true;
    return false;
  }

  valueIncludesBoth(value, values: string[]): boolean {
    let includes = true;
    if (!value || !values?.length) return false;
    if (Array.isArray(value)) value = value.toString();
    values?.forEach(v => includes = includes &&
      (
        v.includes('.') ? v.toLowerCase().replace('.', '') === value.toLowerCase() :
          value?.toLowerCase()?.includes(v?.toLowerCase())
      )
    );
    return includes;
  }

  spacelesslyIncludes(x: string, y: string) {
    if (!(x = this.trim(x?.toLowerCase())) && (y = this.trim(y?.toLowerCase()))) return false;
    else return (y.includes('.')) ? x === y.replace('.', '') : !!x?.includes(y);
  }

  getPrefix(str, separator, includeSeparator = true) {
    const prefix = str?.substring(0, str.lastIndexOf(separator));
    return `${prefix && includeSeparator ? prefix + separator : ''}`;
  }

  getSuffix(str, separator, includeSeparator = true) {
    const suffix = str?.substring(0, str.indexOf(separator));
    return `${suffix && includeSeparator ? separator + suffix : ''}`;
  }

  checkPhonenumberValidity(number: string): string {
    // This<<?(255)>> starting with +255 or 255
    const regex = new RegExp('^\\+?(255|0)(6|7)(\\d{8})$');
    let status = '';

    if (regex.test(number) && number.length == 12) {
      status = 'VALID';
      return status;

    } else {
      status = 'INVALID';
      return status;
    }
  }

  // end strings
}
